import React, { useContext, useEffect, useState } from "react";
import loginPic from "../assets/login-logo-2.png";
import Logo from "../assets/logo.avif";
import { Formik } from "formik";
import { LoginForm } from "../components";
import * as Yup from "yup";
import swal from "sweetalert";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { LoginContext } from "../reactcontext/ReactContext";
import "./Login.css";

const Login = () => {
  const navigate = useNavigate();
  const { loginInfo, setLoginInfo } = useContext(LoginContext);
  const [loading, setLoading] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    if (loginInfo.login) {
      navigate("/dashboard");
    }
  }, [loginInfo]);

  const initialValues = {
    email: "",
    passwd: "",
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email("Email must be valid")
      .required("Email is required"),
    passwd: Yup.string().required("Password is required"),
  });

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      console.log("1")
      // Log in and fetch user data in parallel
      const loginResponse = await axios.post(`${apiUrl}/auth/login`, {
        ...data,
        password: data.passwd,
      });
      console.log("2", loginResponse)
      const loginToken = loginResponse.data;
      if (!loginToken) {
        throw new Error("No token received from server");
      }
      localStorage.setItem("loginToken", loginToken);
  
      // Fetch authenticated user details
      const userResponse = await axios.get(`${apiUrl}/auth/authenticatedUser`, {
        headers: {
          loginToken: loginToken,
        },
      });
      console.log("3", userResponse)
      
      if (!userResponse.data || !userResponse.data.loginInfo) {
        throw new Error("Invalid user data received from server");
      }
      
      const userData = {
        ...loginInfo,
        userDetails: {
          id: userResponse.data.loginInfo.userId,
          email: userResponse.data.loginInfo.email,
          firstName: userResponse.data.loginInfo.User.firstName,
          lastName: userResponse.data.loginInfo.User.lastName,
          role: userResponse.data.loginInfo.UserRole.roleName,
        },
        login: true,
      };
      console.log("4", userData)
      setLoginInfo(userData);
      setLoading(false);
      navigate("/dashboard");
    } catch (err) {
      console.log("Error caught:", err);
      const errorMessage = err.response?.data?.error || err.message || 'An error occurred during login';
      console.log('Login error:', err.response?.data || err.message || err);
      swal({
        icon: 'error',
        title: 'Error !',
        text: errorMessage,
      });
      setLoading(false);
    }
  };

  return (
    <div className="login-container min-h-screen w-full overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 relative flex items-center justify-center">
      
      {/* Content Container - Centered Layout */}
      <div className="relative z-10 w-full flex items-center justify-center px-4 md:px-8 py-8 gap-4 md:gap-8">
        
        {/* Left Side - Information */}
        <div className="hidden lg:flex lg:w-1/2 flex-col justify-center items-start gap-6 pr-0 lg:pr-8 max-w-lg relative">
          
          {/* Logo */}
          <div className="flex justify-start">
            <img src={Logo} alt="LOGO" className="w-16 h-16" />
          </div>

          {/* Main Heading */}
          <div className="text-left">
            <h1 className="text-5xl font-bold text-slate-900 mb-4 leading-tight">
              Welcome to<br />Rahman Group
            </h1>
            <p className="text-slate-600 text-lg font-light">
              Manage your records digitally and efficiently
            </p>
          </div>

          {/* Features List */}
          <div className="space-y-6 w-full">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-slate-900 font-semibold mb-1">Secure Access</h3>
                <p className="text-slate-600 text-sm">Enterprise-grade security for your data</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <h3 className="text-slate-900 font-semibold mb-1">Real-time Updates</h3>
                <p className="text-slate-600 text-sm">Instant synchronization across all devices</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m0-4v2m0-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-slate-900 font-semibold mb-1">Easy Management</h3>
                <p className="text-slate-600 text-sm">Intuitive interface for effortless control</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Card */}
        <div className="w-full lg:w-1/2 flex justify-center items-center">
          <div className="w-full max-w-md">
            
            {/* Main Card */}
            <div className="login-card bg-white rounded-2xl p-8 md:p-10 shadow-lg border border-slate-200">
              
              {/* Logo for Mobile */}
              <div className="flex justify-center mb-6 lg:hidden">
                <img src={Logo} alt="LOGO" className="w-16 h-16" />
              </div>

              {/* Header Section */}
              <div className="mb-8 text-center">
                <h1 className="text-3xl font-bold text-slate-900 mb-2">
                  Sign In
                </h1>
                <p className="text-slate-600 text-sm md:text-base font-light">
                  Enter your credentials to continue
                </p>
              </div>

              {/* Divider */}
              <div className="h-px bg-slate-200 mb-8"></div>

              {/* Loading State */}
              {loading && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="animate-spin">
                      <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                    </div>
                    <span className="text-blue-700 text-sm font-medium">Authenticating...</span>
                  </div>
                </div>
              )}

              {/* Form Container */}
              <div className="form-container">
                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={onSubmit}
                >
                  <LoginForm loading={loading} />
                </Formik>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .animate-fade-in {
          animation: fade-in 0.8s ease-out;
        }
      `}</style>
    </div>
  );
};

export default Login;
